import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useSessionExpiration } from '../hooks/useSessionExpiration'
import toast from 'react-hot-toast'
import axios from 'axios'

interface TicketFormData {
  title: string
  description: string
  departmentId: string
  categoryId: string
  priorityId: string
  sourceId?: string
  clientOrgId?: string
  clientUserId?: string
  assignedTo?: string
}

interface Department {
  id: string
  name: string
  description?: string
}

interface Category {
  id: string
  name: string
  description?: string
  departmentId: string
}

interface Priority {
  id: string
  name: string
  description?: string
  slaHours: number
}

interface Source {
  id: string
  name: string
  description?: string
  color?: string
}

interface ClientOrganization {
  id: string
  name: string
  domain: string
  isActive: boolean
}

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  roles: string[]
}

const NewTicket: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { handleAuthError, trackLogin } = useSessionExpiration()
  
  const [formData, setFormData] = useState<TicketFormData>({
    title: '',
    description: '',
    departmentId: '',
    categoryId: '',
    priorityId: '',
    clientOrgId: '',
    clientUserId: '',
    assignedTo: ''
  })
  
  const [departments, setDepartments] = useState<Department[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [priorities, setPriorities] = useState<Priority[]>([])
  const [sources, setSources] = useState<Source[]>([])
  const [clientOrganizations, setClientOrganizations] = useState<ClientOrganization[]>([])
  const [clientUsers, setClientUsers] = useState<User[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const isStaff = user?.roles?.some(role => ['administrator', 'support_staff', 'manager', 'staff'].includes(role))

  // Track login time to prevent false session expiration
  useEffect(() => {
    trackLogin()
  }, [trackLogin])

  // Load form data
  useEffect(() => {
    const loadFormData = async () => {
      // Check if user is authenticated
      if (!user || !user.id) {
        console.error('User not authenticated')
        toast.error('Please log in to create tickets')
        navigate('/login')
        return
      }

      console.log('Loading form data for user:', user.email)
      
      try {
        const requests = [
          axios.get('/api/tickets/departments'),
          axios.get('/api/tickets/priorities'),
          axios.get('/api/tickets/sources')
        ]

        // Add client organizations request for staff users
        if (isStaff) {
          requests.push(axios.get('/api/organizations'))
        }

        const responses = await Promise.all(requests)
        
        console.log('Departments response:', responses[0].data)
        console.log('Priorities response:', responses[1].data)
        
        setDepartments(responses[0].data.data || [])
        setPriorities(responses[1].data.data || [])
        setSources(responses[2].data.data || [])
        
        if (isStaff && responses[3]) {
          console.log('Organizations response:', responses[3].data)
          setClientOrganizations(responses[3].data.data || [])
        }
        
        setIsLoading(false)
      } catch (error) {
        console.error('Error loading form data:', error)
        
        // Check if it's a session expiration error
        if (handleAuthError(error)) {
          setIsLoading(false)
          return // Session expired modal will be shown
        }
        
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
        console.error('Specific error details:', errorMessage)
        toast.error(`Failed to load form data: ${errorMessage}`)
        setIsLoading(false)
      }
    }

    loadFormData()
  }, [user, navigate, isStaff])

  // Fetch client users when client organization is selected
  useEffect(() => {
    const fetchClientUsers = async () => {
      if (!formData.clientOrgId || !isStaff) {
        setClientUsers([])
        return
      }

      try {
        const response = await axios.get(`/api/organizations/${formData.clientOrgId}/users`)
        if (response.data.success) {
          setClientUsers(response.data.data || [])
        }
      } catch (error) {
        console.error('Error fetching client users:', error)
        toast.error('Failed to load client users')
        setClientUsers([])
      }
    }

    fetchClientUsers()
  }, [formData.clientOrgId, isStaff])

  // Load categories when department changes
  useEffect(() => {
    const loadCategories = async () => {
      if (!formData.departmentId) {
        setCategories([])
        return
      }

      try {
        const response = await axios.get(`/api/tickets/departments/${formData.departmentId}/categories`)
        setCategories(response.data.data || [])
        setFormData(prev => ({ ...prev, categoryId: '' })) // Reset category when department changes
      } catch (error) {
        console.error('Error loading categories:', error)
        toast.error('Failed to load categories')
      }
    }

    loadCategories()
  }, [formData.departmentId])

  // Load staff users for assignment
  useEffect(() => {
    const loadStaffUsers = async () => {
      try {
        const response = await axios.get('/api/users/staff')
        if (response.data.success) {
          setUsers(response.data.data || [])
        } else {
          throw new Error(response.data.error || 'Failed to load staff users')
        }
      } catch (error) {
        console.error('Error loading staff users:', error)
        toast.error('Failed to load staff users')
        setUsers([])
      }
    }

    if (isStaff) {
      loadStaffUsers()
    }
  }, [isStaff])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const requiredFields = ['title', 'description', 'departmentId', 'categoryId', 'priorityId']
    if (isStaff) {
      requiredFields.push('clientOrgId', 'clientUserId')
    }
    
    const missingFields = requiredFields.filter(field => !formData[field as keyof TicketFormData])
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`)
      return
    }

    setIsSubmitting(true)
    
    try {
      const response = await axios.post('/api/tickets', formData)
      
      if (response.data.success) {
        toast.success('Ticket created successfully!')
        navigate('/tickets')
      } else {
        toast.error(response.data.error || 'Failed to create ticket')
      }
    } catch (error: any) {
      console.error('Error creating ticket:', error)
      toast.error(error.response?.data?.error || 'Failed to create ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/tickets')
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading form data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Create New Ticket</h1>
          <p className="mt-2 text-sm text-gray-700">
            Submit a new support request
          </p>
        </div>
      </div>
      
      <div className="mt-8 card">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Client Organization Selector for Staff Users */}
            {isStaff && (
              <div>
                <label htmlFor="clientOrgId" className="block text-sm font-medium text-gray-700">
                  Client Organization *
                </label>
                <select
                  id="clientOrgId"
                  name="clientOrgId"
                  required
                  className="input-field mt-1"
                  value={formData.clientOrgId}
                  onChange={(e) => setFormData(prev => ({ ...prev, clientOrgId: e.target.value, clientUserId: '' }))}
                  disabled={isSubmitting}
                >
                  <option value="">Select Client Organization</option>
                  {clientOrganizations
                    .filter(org => org.isActive)
                    .map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name} ({org.domain})
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Client User Selector for Staff Users */}
            {isStaff && formData.clientOrgId && (
              <div>
                <label htmlFor="clientUserId" className="block text-sm font-medium text-gray-700">
                  Client User (Caller) *
                </label>
                <select
                  id="clientUserId"
                  name="clientUserId"
                  required
                  className="input-field mt-1"
                  value={formData.clientUserId}
                  onChange={(e) => setFormData(prev => ({ ...prev, clientUserId: e.target.value }))}
                  disabled={isSubmitting}
                >
                  <option value="">Select Client User</option>
                  {clientUsers
                    .map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                </select>
                {clientUsers.length === 0 && formData.clientOrgId && (
                  <p className="text-xs text-gray-500 mt-1">
                    No active users found for this organization
                  </p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="departmentId" className="block text-sm font-medium text-gray-700">
                Department *
              </label>
              <select
                id="departmentId"
                name="departmentId"
                required
                className="input-field mt-1"
                value={formData.departmentId}
                onChange={(e) => setFormData(prev => ({ ...prev, departmentId: e.target.value }))}
                disabled={isSubmitting}
              >
                <option value="">Select Department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700">
                Category *
              </label>
              <select
                id="categoryId"
                name="categoryId"
                required
                className="input-field mt-1"
                value={formData.categoryId}
                onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                disabled={isSubmitting || !formData.departmentId}
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="priorityId" className="block text-sm font-medium text-gray-700">
                Priority *
              </label>
              <select
                id="priorityId"
                name="priorityId"
                required
                className="input-field mt-1"
                value={formData.priorityId}
                onChange={(e) => setFormData(prev => ({ ...prev, priorityId: e.target.value }))}
                disabled={isSubmitting}
              >
                <option value="">Select Priority</option>
                {priorities.map(priority => (
                  <option key={priority.id} value={priority.id}>
                    {priority.name} ({priority.slaHours}h SLA)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="sourceId" className="block text-sm font-medium text-gray-700">
                Method of Contact
              </label>
              <select
                id="sourceId"
                name="sourceId"
                className="input-field mt-1"
                value={formData.sourceId || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, sourceId: e.target.value || undefined }))}
                disabled={isSubmitting}
              >
                <option value="">Select Method of Contact</option>
                {sources.map(source => (
                  <option key={source.id} value={source.id}>
                    {source.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Staff Assignment for Staff Users */}
            {isStaff && formData.clientOrgId && (
              <div>
                <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700">
                  Assign To Staff Member
                </label>
                <select
                  id="assignedTo"
                  name="assignedTo"
                  className="input-field mt-1"
                  value={formData.assignedTo}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                  disabled={isSubmitting}
                >
                  <option value="">Unassigned (No Staff Member)</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Ticket Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              required
              className="input-field mt-1"
              placeholder="Brief description of the issue"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows={4}
              className="input-field mt-1"
              placeholder="Detailed description of the issue, steps to reproduce, and any relevant information"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default NewTicket
